'use client';

import { goMainOneTimeAuth } from '@/utils/functions';
import {
  type CoinSection,
  CommonMenu as CommonMenuComponent,
  type CommonMenuProps,
  type MenuItem,
  type ProfileSection,
  formatCurrency,
  iconArrowRotate,
  iconCard,
  iconCoin,
  iconCoinBlanc,
  iconCoinSmall,
  iconEdit,
  iconHistory,
  iconLogin,
  iconLogout,
  iconMailBox,
  iconPresent,
  iconQuestion,
  iconSerial,
  iconSetting,
  iconShipping,
  iconUsers,
  iconWChance,
} from '@monorepo/common';
import { useRouter } from 'next/navigation';

function CommonMenu({
  styleVariant = 'raffle',
  formatCurrency: formatCurrencyProp,
  serviceId,
  locationHref,
  onSerialCode,
  ...props
}: Omit<CommonMenuProps, 'formatCurrency' | 'menuItems'> & {
  styleVariant?: 'default' | 'raffle';
  formatCurrency?: CommonMenuProps['formatCurrency'];
  serviceId?: string;
  locationHref?: string;
  onSerialCode: () => void;
}) {
  const router = useRouter();
  const finalFormatCurrency = formatCurrencyProp || formatCurrency;
  const finalServiceId = serviceId || process.env.NEXT_PUBLIC_SERVICE_ID || 'raffle';
  const finalLocationHref = locationHref || (typeof window !== 'undefined' ? window.location.href : '');

  // プロフィールセクションの生成
  const profileSection: ProfileSection = {
    userName: props.userName || '',
    editLink: '/account/edit',
    editIconSrc: iconEdit.src,
  };

  // コインセクションの生成
  const coinSection: CoinSection = {
    label: '保有しているコイン',
    coinAmount: props.userCoin ?? 0,
    coinIconSrc: iconCoinSmall.src,
    formatCurrency: finalFormatCurrency,
  };

  const isShowTrade = () => {
    // 本番の場合は表示しない
    if (process.env.NEXT_PUBLIC_ENV === 'production') return false;
    // 海外アカウントの場合は表示しない
    if (props.isExternal) return false;
    return true;
  };

  // メニューリストの生成
  const menuItems: MenuItem[] = [
    {
      id: 'messages',
      iconSrc: iconMailBox.src,
      text: 'メッセージボックス',
      onClick: () => router.push('/account/messages'),
      showBadge: props.userUnreadMessageBoxCount ? props.userUnreadMessageBoxCount > 0 : false,
    },
    {
      id: 'collection',
      iconSrc: iconPresent.src,
      text: 'オンラインくじ獲得景品一覧',
      onClick: () => router.push('/account/collection'),
      showBadge: props.isOptionNotEntered,
    },
    ...(isShowTrade()
      ? [
          {
            id: 'trade',
            iconSrc: iconArrowRotate.src,
            text: '景品を交換する',
            onClick: () => router.push('/trade'),
          },
        ]
      : []),
    {
      id: 'wchance',
      iconSrc: iconWChance.src,
      text: 'Wチャンス賞チャレンジ履歴',
      onClick: () => router.push('/account/wchance-collection'),
      showBadge: props.hasPendingEntry,
    },
    {
      id: 'purchase',
      iconSrc: iconCoin.src,
      text: 'コインを購入する',
      onClick: () => router.push(props.isFamikuji ? '/familymart/purchase' : '/account/purchase'),
    },
    {
      id: 'coin-history',
      iconSrc: iconCoinBlanc.src,
      text: 'コイン消費履歴',
      onClick: () => router.push('/account/coin-history'),
    },
    {
      id: 'payment-cards',
      iconSrc: iconCard.src,
      text: 'お支払い方法',
      onClick: () => goMainOneTimeAuth({ redirectPath: `/${finalServiceId}/payment/cards` }),
    },
    {
      id: 'serial-code',
      iconSrc: iconSerial.src,
      text: 'シリアルコードの入力',
      onClick: onSerialCode,
    },
    {
      id: 'payment-history',
      iconSrc: iconHistory.src,
      text: '決済履歴',
      onClick: () => goMainOneTimeAuth({ redirectPath: '/account/payment/history' }),
    },
    {
      id: 'address',
      iconSrc: iconShipping.src,
      text: '配送先の確認と変更',
      onClick: () =>
        goMainOneTimeAuth({ redirectPath: `/account/address?callback=${finalLocationHref}`, shouldTrack: true }),
    },
    ...(isShowTrade()
      ? [
          {
            id: 'delivery',
            iconSrc: iconShipping.src,
            text: '配送履歴',
            onClick: () => router.push('/delivery'),
          },
        ]
      : []),
    {
      id: 'login-settings',
      iconSrc: iconLogin.src,
      text: 'ログイン情報設定',
      onClick: () => goMainOneTimeAuth({ redirectPath: '/account' }),
    },
    {
      id: 'notification',
      iconSrc: iconSetting.src,
      text: '通知設定',
      onClick: () => router.push('/account/notification'),
    },
    {
      id: 'faq',
      iconSrc: iconQuestion.src,
      text: 'よくある質問',
      onClick: () => router.push('/faq'),
    },
    {
      id: 'oauth',
      iconSrc: iconUsers.src,
      text: 'アカウント連携',
      onClick: () => router.push('/account/oauth'),
    },
    {
      id: 'logout',
      iconSrc: iconLogout.src,
      text: 'ログアウト',
      onClick: props.onLogout,
    },
  ];

  return (
    <CommonMenuComponent
      formatCurrency={finalFormatCurrency}
      menuItems={menuItems}
      profileSection={profileSection}
      coinSection={coinSection}
      {...props}
    />
  );
}

export default CommonMenu;
